import {SortOrderEnum} from './Common.types'

/** Microsoft consent type: AllPrincipals (all users) or Principal (specific user) */
export type MicrosoftConsentType = 'AllPrincipals' | 'Principal'

/** How consent was granted */
export enum SSPMAppConsentMethodEnum {
  Admin = 'admin',
  User = 'user',
  Combined = 'combined', // both admin and user consent
}

/** Dope categorization of app access scope */
export enum SSPMAppCategoryEnum {
  Global = 'global', // organization-wide
  Scoped = 'scoped', // limited access
  Login = 'login', // identity-only
}

/** Risk level assessment for the app */
export enum SSPMAppRiskLevelEnum {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
  Critical = 'critical',
}

/** Telemetry data availability for this app in the analysis window. */
export enum SSPMTelemetryAvailability {
  Available = 'available',
  Partial = 'partial',
  Unavailable = 'unavailable',
}

/** Activity posture derived from user/app sign-in recency. */
export enum SSPMAppActivityStatus {
  Active = 'active',
  Inactive = 'inactive',
  Unknown = 'unknown',
}

/**
 * Permission usage posture over the telemetry analysis window.
 * - rightsized: the granted permission set mostly matches observed usage, with little or no excess.
 * - overprovisioned: the app has materially more access than needed.
 * - unknown: usage data is insufficient to determine posture.
 */
export enum SSPMPermissionUsageStatus {
  Rightsized = 'rightsized',
  Overprovisioned = 'overprovisioned',
  Unknown = 'unknown',
}

/** App level permissions */
export interface AppRole {
  /** The MS unique id for this app role */
  msAppRoleId: string

  /** The name of the resource the permission is against, e.g. Microsoft Graph */
  resourceName: string

  /** The uuid of the resource, e.g. 25d76413-902c-47c2-8a3a-413b12cdea24 */
  resourceId: string | null

  /** The permission granted, e.g. User.Read.All */
  permission: string

  /** The description of the granted permission, e.g. "Read all users' full profiles" */
  description: string
}

/** Application User data */
export interface SSPMUserData {
  /** The user id */
  msUserId: string

  /** The user display name */
  displayName: string

  /** The user email address */
  email: string
}

/** Delegated grant permissions */
export interface DelegatedGrant {
  /** The name of the resource the permission is against, e.g. Microsoft Graph */
  resourceName: string

  /** Indicates if the consent is for all or a specific principal */
  consentType: MicrosoftConsentType

  /** The user data for the principal, optional if the consentType is AllPrincipals */
  userData?: SSPMUserData

  /** The list of scopes granted, e.g. "openid profile User.Read offline_access Files.ReadWrite" */
  scopes: string
}

/** Publisher information */
export interface PublisherInfo {
  /** Verified publisher */
  verified: boolean

  /** Publisher name */
  publisherName?: string

  /** URL to the app's logo image */
  logoUrl?: string

  /** URL to the app's homepage */
  homepageUrl?: string

  /** URL to the app's terms of service */
  termsOfServiceUrl?: string

  /** URL to the app's privacy statement */
  privacyStatementUrl?: string
}

/** Gallery App information */
export interface GalleryAppInfo {
  /** Entra App Gallery app name - may differ from customers set app display name */
  displayName: string

  /** Entra App Gallery provided description for app */
  description: string

  /** Entra App Gallery categories */
  categories: string[]
}

/** SSO mode for login apps */
export type SSOMode = 'saml' | 'oidc' | 'password' | 'none'

/** Login app specific information */
export interface LoginInfo {
  /** The SSO mode for logging in */
  ssoMode: SSOMode

  /** Indicates if the app supports automatic provisioning of users i.e. scim */
  provisionUsers: boolean
}

/** Information about the organization that owns the app */
export interface OrganizationData {
  /** The organization's MS tenant id */
  msTenantId: string
}

/** Dope's risk assessment for the application */
export interface SSPMAppRiskAssessment {
  /** Overall risk level categorization */
  riskLevel: SSPMAppRiskLevelEnum

  /** Calculated risk score (product of impact and likelihood) - Range: 1-25 */
  riskScore: number

  /** Impact score: potential damage if compromised - Range: 1-5 */
  impactScore: number

  /** Likelihood score: probability of being exploited - Range: 1-5 */
  likelihoodScore: number

  /** List of specific risk factors identified for this app (returned by Microsoft) */
  riskFactors: string[]
}

/** Permission usage summary over the analysis window */
export interface SSPMPermissionSummary {
  /** Permission usage posture over the lookback window */
  usageStatus: SSPMPermissionUsageStatus

  /** Count of granted permissions, across both app permissions and delegated grants. */
  grantedCount: number

  /** Count of granted permissions with no observed (resource level) usage during the analysis window */
  unusedCount: number
}

/** Sign-in telemetry summary over the analysis window */
export interface SSPMSignInSummary {
  /** Most recent observed sign-in for the app (user or service principal) */
  lastSignInDateTime?: string

  /** Successful user (delegated) sign-ins. Only present when the app has delegated grants. */
  userSuccessCount?: number

  /** Failed user (delegated) sign-ins. Only present when the app has delegated grants. */
  userFailedCount?: number

  /** Successful application-only sign-ins. Only present when the app has application permissions. */
  applicationSuccessCount?: number

  /** Failed application-only sign-ins. Only present when the app has application permissions. */
  applicationFailedCount?: number

  /** List of country codes (two letter code) where the sign-in originated */
  countries?: string[]
}

/** Compact telemetry summary for app list triage */
export interface MicrosoftAppTelemetrySummary {
  /** Snapshot timestamp the telemetry summary is derived from */
  asOfDateTime: string

  /** Number of days included in telemetry analysis */
  lookbackDays: number

  /** Availability of telemetry data for this app */
  availability: SSPMTelemetryAvailability

  /** Derived activity status for this app */
  activityStatus?: SSPMAppActivityStatus

  /** Permission usage summary for the app over the analysis window */
  permission?: SSPMPermissionSummary

  /** Sign-in telemetry for the app over the analysis window */
  signIn?: SSPMSignInSummary
}

export interface MicrosoftAppListItem {
  /** MS unique id for this app instance */
  msObjectId: string

  /** MS unique id for this app */
  msAppId: string

  /** Display name of the application */
  displayName: string

  /** The date and time the app was created in Microsoft for the customer (ISO 8601 format) */
  createdDateTime?: string

  /** The date and time the app was last synchronised with SSPM (ISO 8601 format) */
  lastSyncDateTime: string

  /** The user who created (or first authorized) the app */
  creator?: SSPMUserData

  /** Dopamine AI-generated analysis of the app */
  dopamine: string

  /** Information from the publisher of the app */
  publisherInfo: PublisherInfo

  /** Information about the MS tenant that originated the app */
  appOwnerOrganization: OrganizationData

  /** The enablement status of the app */
  appEnabled: boolean

  /** Indicates if the app has been disabled by Microsoft due to DisabledDueToViolationOfServicesAgreement */
  disabledByMicrosoft: boolean

  /** Owners assigned to the app */
  owners: SSPMUserData[]

  /** Reply (redirect) URLs registered for this application - can be empty */
  replyUrls: string[]

  /** Dope overall categorization of the app */
  category: SSPMAppCategoryEnum

  /** Dope categorization of the consent method used for the app */
  consentMethod: SSPMAppConsentMethodEnum

  /** Dope risk assessment details for the app */
  riskAssessment: SSPMAppRiskAssessment

  /** Optional compact telemetry summary for list-level triage */
  telemetrySummary?: MicrosoftAppTelemetrySummary

  /** Is this app created "in-house" or from an external provider */
  selfPublished: boolean

  /** Is this app single tenant or multi-tenant */
  singleTenant: boolean

  /** Optional information if this app is a login app */
  loginInfo?: LoginInfo

  /** Optional information if this app is from the MS Entra Gallery */
  galleryAppInfo?: GalleryAppInfo
}

/** Applications list type */
export type SSPMApplicationsListType = MicrosoftAppListItem[]

/** Response containing Microsoft/Google third-party apps with pagination */
export interface SSPMApplicationsAPIResponse {
  /** Array of Microsoft third-party applications */
  apps: SSPMApplicationsListType

  /** Cursor-based pagination information */
  pageInfo: SSPMApplicationsPageInfo
}

export interface SSPMApplicationsAPIResponseData {
  data: SSPMApplicationsAPIResponse
}

/** Response containing app details including roles and delegated grants with pagination */
export interface SSPMAppDetailsResponse {
  /** Microsoft unique id for this app */
  msObjectId: string

  /** Display name of the application */
  appDisplayName: string

  /** App level permissions assigned to the app - can be empty and not present in subsequent pages */
  appRoles?: AppRole[]

  /** Array of delegated permission grants - can be empty */
  delegatedGrants: DelegatedGrant[]

  /** Cursor-based pagination information - needed for additional pages of delegated grants */
  pageInfo: SSPMApplicationsPageInfo
}

export interface SSPMAppDetailsResponseData {
  data: SSPMAppDetailsResponse
}

export interface SSPMAppDetailsAPIParams {
  /** Cursor for the next page of delegated grants */
  after?: string
}

/** Overview of the SSPM module for the Microsoft tenant */
export interface SSPMOverviewResponse {
  /** Total count of third-party apps discovered */
  totalApps: number

  /** Whether or not the initial onboarding scan is currently in progress */
  initialScanInProgress: boolean

  /** The last time the apps were synchronised with the Microsoft tenant */
  lastSyncDateTime?: string
}

export interface SSPMOverviewAPIResponseData {
  data: SSPMOverviewResponse
}

/** Cursor-based pagination information */
export interface SSPMApplicationsPageInfo {
  /** Cursor for the next page */
  endCursor?: string

  /** Whether there are more results available */
  hasNextPage: boolean
}

/** Parameters for getting SSPM applications */
export interface SSPMApplicationsAPIParams {
  /**
   * Sort order for results. Format: "field:direction" or "field" (defaults to asc).
   * Allowed fields: createdDateTime, total sign-ins
   * Direction: asc or desc.
   */
  sort?: string

  /** Cursor for the next page */
  after?: string

  /** Filter by risk level (comma-separated values) */
  riskLevel?: string

  /** Filter by consent method (comma-separated values) */
  consentMethod?: string

  /** Filter by activity status (comma-separated values) */
  activityStatus?: string

  /** Filter by permission usage posture (comma-separated values) */
  permissionUsageStatus?: string

  /** Filter by category (comma-separated values) */
  category?: string

  /**
   * General fuzzy search by application display name, publisher name or creator's email address (case-insensitive substring match).
   * Only one of the search query-params, `search`, `displayName`, `publisherName` or `creatorEmail` can be provided at a time.
   */
  search?: string

  /** Exact search by creator email address (case-insensitive substring match) */
  creatorEmail?: string
}

export type AppliedFilters = {
  category: string[]
  riskLevel: string[]
  consent: string[]
  activityStatus: string[]
  permissionUsageStatus: string[]
  search: string
  creatorEmail: string
  sort: string
  direction?: SortOrderEnum
}
